/**
 * ReconsentPrompt
 * Phase 999.72 Plan 11 — D-33 / D-34 reconsent flow.
 *
 * Modal prompt shown to existing users whose `profile.acceptedConsentVersion`
 * does NOT match the current `CURRENT_CONSENT_VERSION` constant. The user must
 * re-accept the updated Privacy Policy + Terms of Service before continuing.
 *
 * Legacy users (no stored acceptedConsentVersion — e.g., pre-999.71 signups)
 * are handled by the existing onboarding flow and must NOT see this modal.
 * The rendering decision lives in App.js (see `needsReconsent` logic); this
 * component is a dumb presentational modal.
 *
 * D-34 sequence:
 *   1. Legal review passes (drafts at .planning/legal-drafts/)
 *   2. Web team publishes v2.0 to afterservice.ai/privacy + /terms
 *   3. Engineering bumps CURRENT_CONSENT_VERSION ('1.0' → '2.0')
 *   4. Next login → this modal renders → user re-accepts
 *   5. Plan 06 backend persists new acceptedConsentVersion + writes audit log
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

/**
 * The copy this modal renders, as one string, so the acceptance write can send
 * it for the consent audit hash (Privacy Policy 7.1: "a cryptographic hash of
 * the exact text shown to you"). Keep in step with the JSX below.
 *
 * @param {string} currentVersion
 * @param {string | null | undefined} acceptedVersion
 * @returns {string}
 */
export function reconsentTextFor(currentVersion, acceptedVersion) {
  return [
    'Updated Privacy Policy and Terms',
    "We've updated our Privacy Policy and Terms of Service. Before you continue, please review and accept the new versions.",
    `Your last accepted version: ${acceptedVersion || 'none'}. Current version: ${currentVersion}.`,
    // NOTE: this string feeds the consent payload hash (999.116 runbook).
    // Keep it byte-stable — changing it re-prompts every user for consent.
    // The www form is the historical hash text; display links are canonical.
    `Privacy Policy v${currentVersion} — https://www.afterservice.ai/privacy/`,
    `Terms of Service v${currentVersion} — https://afterservice.ai/terms`,
    'I accept the new versions',
  ].join('\n');
}

/**
 * @param {{
 *   open: boolean,
 *   onAccept: () => void,
 *   onClose: () => void,
 *   currentVersion: string,
 *   acceptedVersion?: string | null,
 * }} props
 */
export default function ReconsentPrompt({
  open,
  onAccept,
  onSignOut,
  currentVersion,
  acceptedVersion,
}) {
  return (
    <Dialog
      open={open}
      aria-labelledby="reconsent-title"
      aria-describedby="reconsent-description"
      fullWidth
      maxWidth="sm"
      // Phase 999.107 commit 3 — Strict consent policy: the reconsent modal
      // is no longer dismissible. Previously onClose was a soft dismiss
      // ("Review later") which let veterans defer indefinitely while still
      // using the service on the OLD consented version. The only exits now
      // are accept (onAccept) or sign out (onSignOut). disableEscapeKeyDown
      // stops the Esc key from being a backdoor; omitting the onClose prop
      // on Dialog stops backdrop clicks from closing the modal (MUI Dialog
      // calls onClose for both Escape and backdropClick; with neither path
      // available, the modal stays put).
      disableEscapeKeyDown
    >
      <DialogTitle id="reconsent-title">
        Updated Privacy Policy and Terms
      </DialogTitle>
      <DialogContent id="reconsent-description">
        <Typography variant="body2" sx={{ mb: 2 }}>
          We&apos;ve updated our Privacy Policy and Terms of Service. Before
          you continue, please review and accept the new versions.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Your last accepted version: {acceptedVersion || 'none'}. Current
          version: {currentVersion}.
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>
            <a
              href="https://afterservice.ai/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy v{currentVersion}
            </a>
          </li>
          <li>
            <a
              href="https://afterservice.ai/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service v{currentVersion}
            </a>
          </li>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Phase 999.107 commit 3 — "Review later" replaced with "Sign out".
            The veteran can no longer defer past a version bump; they
            either accept the new versions or sign out. */}
        <Button onClick={onSignOut} color="inherit">
          Sign out
        </Button>
        <Button onClick={onAccept} variant="contained" autoFocus>
          I accept the new versions
        </Button>
      </DialogActions>
    </Dialog>
  );
}
